import type { OkBody } from '../Models/ApiModel';
import type {
  AvailabilityRangeModel,
  StudentAbsenceModel,
  StudentContactModel,
  StudentDraftModel,
  StudentModel,
  StudentSlotModel
} from '../Models/StudentModel';
import { http } from './httpService';

/** The roster and everything hanging off a student. */
export const StudentService = {
  list: () => http.get<StudentModel[]>('/students'),

  create: (student: StudentDraftModel) => http.post<StudentModel>('/students', student),

  /**
   * A patch, so an unmentioned field keeps its stored value. A response
   * carrying `previousName` means the student was renamed and everything
   * keyed by the old name has moved with them.
   */
  update: (name: string, patch: StudentDraftModel) =>
    http.patch<StudentModel & { previousName?: string }>(`/students/${encodeURIComponent(name)}`, patch),

  /** Deletes their lessons too, which is the tutor's own income record. */
  remove: (name: string) => http.delete<OkBody>(`/students/${encodeURIComponent(name)}`),

  setGcalRecurring: (name: string, gcalRecurring: boolean) =>
    http.patch<{ name: string; gcalRecurring: 0 | 1 }>(
      `/students/${encodeURIComponent(name)}/gcal-recurring`,
      { gcalRecurring }
    ),

  /* Slots */

  listSlots: (name: string) =>
    http.get<StudentSlotModel[]>(`/students/${encodeURIComponent(name)}/slots`),

  addSlot: (name: string, slot: { weekday: number; time: string; durationMinutes?: number }) =>
    http.post<StudentSlotModel>(`/students/${encodeURIComponent(name)}/slots`, slot),

  updateSlot: (id: number, patch: Partial<{ weekday: number; time: string; durationMinutes: number }>) =>
    http.patch<StudentSlotModel>(`/slots/${id}`, patch),

  removeSlot: (id: number) => http.delete<OkBody>(`/slots/${id}`),

  /** Stores the calendar event standing for a recurring slot, so it can be found again. */
  linkSlotCalendarEvent: (id: number, gcalEventId: string | null) =>
    http.patch<{ id: number; gcalEventId: string | null }>(`/slots/${id}/gcal-link`, { gcalEventId }),

  /* Availability */

  listAvailability: (name: string) =>
    http.get<AvailabilityRangeModel[]>(`/students/${encodeURIComponent(name)}/availability`),

  setAvailability: (name: string, availability: AvailabilityRangeModel[]) =>
    http.put<AvailabilityRangeModel[]>(
      `/students/${encodeURIComponent(name)}/availability`,
      { availability }
    ),

  /* Absences: vacations, injuries, anything known about in advance. */

  listAbsences: (name: string) =>
    http.get<StudentAbsenceModel[]>(`/students/${encodeURIComponent(name)}/absences`),

  addAbsence: (name: string, absence: { startDate: string; endDate: string; note?: string | null; noteEn?: string | null }) =>
    http.post<StudentAbsenceModel>(`/students/${encodeURIComponent(name)}/absences`, absence),

  updateAbsence: (id: number, patch: Partial<{ startDate: string; endDate: string; note: string | null; noteEn: string | null }>) =>
    http.patch<StudentAbsenceModel>(`/absences/${id}`, patch),

  removeAbsence: (id: number) => http.delete<OkBody>(`/absences/${id}`),

  /* Contacts. Premium: these throw ApiError with code PREMIUM_REQUIRED. */

  listContacts: (name: string) =>
    http.get<StudentContactModel[]>(`/students/${encodeURIComponent(name)}/contacts`),

  addContact: (name: string, contact: { name: string; nameEn?: string; relation?: string; relationEn?: string; phone?: string; receivesPayments?: boolean }) =>
    http.post<StudentContactModel>(`/students/${encodeURIComponent(name)}/contacts`, contact),

  /** One round trip for several people picked out of Google Contacts. */
  addContacts: (name: string, contacts: { name: string; relation?: string; phone?: string; googleResourceName?: string }[]) =>
    http.post<StudentContactModel[]>(`/students/${encodeURIComponent(name)}/contacts`, contacts),

  updateContact: (id: number, patch: Partial<StudentContactModel>) =>
    http.patch<StudentContactModel>(`/contacts/${id}`, patch),

  removeContact: (id: number) => http.delete<OkBody>(`/contacts/${id}`)
};
